/* WHAT FALLS AND WHAT IT DOES WHEN IT LANDS (LS-398, 2026-09-13, Vijay: "do 1 and 2").
 *
 * The game moves things across the ground; nothing in it had a HEIGHT that obeyed gravity. A thrown spear travelled flat,
 * a dropped thing appeared where it landed, a creature knocked off its feet slid rather than tumbled. This is the small
 * rigid layer that gives them one: a height over the ground they stand on, a fall, a bounce that keeps a share of the
 * speed, friction that takes the spin off, and a sleep once the hop is smaller than a pixel.
 *
 * It is deliberately only the VERTICAL story. Where a thing goes across the map stays the game's own business -- the
 * throw's reach, the pickup's tile, every check that asks where a thing ended up. What this adds is what the eye wants:
 * an arc, a landing, a settle.
 */
export type Fall = {
  z: number;        // height over the ground
  vz: number;       // how fast it is rising
  spin: number;     // radians, for what can show it
  vspin: number;
  asleep: boolean;
  stepped?: number;   // the frame it was last carried forward, so a thing drawn twice in a frame falls once
};

export const GRAVITY = 0.55;      // world pixels a frame, a frame -- tuned so a throw arcs over about a second
export const BOUNCE = 0.34;       // the share of the speed a landing keeps
export const FRICTION = 0.72;     // what a landing takes off the spin

export function makeFall(z = 0, vz = 0, vspin = 0): Fall {
  return { z, vz, spin: 0, vspin, asleep: false };
}

/** One step of the fall. `groundZ` is the height of the ground under the thing right now, so a body thrown off a terrace
 *  keeps falling until the lower floor catches it. Returns true while it is still moving. */
export function stepFall(f: Fall, groundZ = 0, g = GRAVITY): boolean {
  if (f.asleep) { f.z = groundZ; return false; }
  f.vz -= g;
  f.z += f.vz;
  f.spin += f.vspin;
  if (f.z <= groundZ) {
    f.z = groundZ;
    if (Math.abs(f.vz) < 1.1) { f.vz = 0; f.vspin = 0; f.asleep = true; return false; }   // it has stopped hopping
    f.vz = -f.vz * BOUNCE;
    f.vspin *= FRICTION;
  }
  return true;
}

/** The height a throw should leave the hand with to be in the air for `frames`. */
export function launchFor(frames: number, g = GRAVITY): number { return g * frames / 2; }

// Pickup arcs follow simulation time, not render frequency; reuse the existing solver.
const PICKUP_ARC: number[] = [];
{
  const fall = makeFall(5, launchFor(26));
  for (let age = 0; age < 120; age++) {
    stepFall(fall);
    PICKUP_ARC.push(fall.z);
    if (fall.asleep) break;
  }
}
export function pickupLift(item: { tossedAt?: number }, frame: number): number {
  if (!Number.isFinite(item.tossedAt) || !Number.isFinite(frame)) return 0;
  const age = Math.floor(frame - item.tossedAt!);
  return age >= 0 ? (PICKUP_ARC[age] ?? 0) : 0;
}

/** A per-object store that forgets a thing as soon as the game does. */
export class Falls {
  private m = new WeakMap<object, Fall>();
  for(o: object, make: () => Fall): Fall {
    let f = this.m.get(o); if (!f) { f = make(); this.m.set(o, f); } return f;
  }
  has(o: object) { return this.m.has(o); }
}
